import { formatTimeLogDateLabel } from "@jugl-web/domain-resources/task-time-tracking/utils";
import { DatePicker } from "@jugl-web/ui-components/cross-platform/DatePicker";
import {
  InteractiveContainer,
  InteractiveContainerProps,
} from "@jugl-web/ui-components/cross-platform/InteractiveContainer";
import { Popover } from "@jugl-web/ui-components/cross-platform/Popover";
import { MobileDrawer } from "@jugl-web/ui-components/mobile/MobileDrawer";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { endOfDay } from "date-fns";
import { FC, forwardRef, useMemo, useState } from "react";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";
import { ReactComponent as ChevronDownIcon } from "./assets/chevron-down.svg";

interface TaskTimeLogFormDatePickerProps {
  fromDate: Date;
  toDate: Date;
  onDaySelect: (date: Date) => void;
}

export const TaskTimeLogFormDatePicker: FC<TaskTimeLogFormDatePickerProps> = ({
  fromDate,
  toDate,
  onDaySelect,
}) => {
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const { isMobile } = useAppVariant();
  const { t } = useTranslations();

  const endOfToday = useMemo(() => endOfDay(new Date()), []);

  if (isMobile) {
    return (
      <>
        <TaskTimeLogFormDatePickerTrigger
          fromDate={fromDate}
          toDate={toDate}
          isActive={isMobileDrawerOpen}
          onClick={() => setIsMobileDrawerOpen(true)}
        />
        <MobileDrawer
          isOpen={isMobileDrawerOpen}
          header={{
            title: t({
              id: "tasks-page.select-date",
              defaultMessage: "Select date",
            }),
          }}
          onClose={() => setIsMobileDrawerOpen(false)}
        >
          <MobileDrawer.Content>
            <DatePicker
              initialDate={fromDate}
              maxDate={endOfToday}
              onDaySelect={(selectedDate) => {
                onDaySelect(selectedDate);
                setIsMobileDrawerOpen(false);
              }}
            />
          </MobileDrawer.Content>
        </MobileDrawer>
      </>
    );
  }

  return (
    <Popover
      placement="bottom-start"
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger
          as={TaskTimeLogFormDatePickerTrigger}
          ref={triggerRef}
          fromDate={fromDate}
          toDate={toDate}
          isActive={isOpen}
        />
      )}
      className="z-[2001] w-[375px] px-4 pt-4 pb-8"
    >
      {({ onClose }) => (
        <DatePicker
          initialDate={fromDate}
          maxDate={endOfToday}
          onDaySelect={(selectedDate) => {
            onDaySelect(selectedDate);
            onClose();
          }}
        />
      )}
    </Popover>
  );
};

interface TaskTimeLogFormDatePickerTriggerProps
  extends InteractiveContainerProps {
  fromDate: Date;
  toDate: Date;
  isActive: boolean;
}

const TaskTimeLogFormDatePickerTrigger = forwardRef<
  HTMLElement,
  TaskTimeLogFormDatePickerTriggerProps
>(({ fromDate, toDate, isActive, ...props }, ref) => {
  const { t } = useTranslations();

  return (
    <InteractiveContainer
      ref={ref}
      className={cx(
        "flex h-10 w-full items-center gap-2 rounded-lg px-2 font-secondary text-[15px] transition-colors",
        isActive ? "bg-grey-100" : "hover:bg-grey-100"
      )}
      {...props}
    >
      <CalendarIcon className="shrink-0" />
      <span className="text-dark-600">
        {t({ id: "tasks-page.date", defaultMessage: "Date" }).concat(":")}
      </span>
      <div className="flex items-center gap-1">
        <span className="font-medium text-dark-900">
          {formatTimeLogDateLabel({
            startDate: fromDate,
            endDate: toDate,
          })}
        </span>
        <ChevronDownIcon
          className={cx(
            "shrink-0 transition-transform",
            isActive && "rotate-180"
          )}
        />
      </div>
    </InteractiveContainer>
  );
});
